// Two identity systems, one chosen per session:
//   internal: Entra ID (Lipari members)                 → always on
//   external: Entra External ID (customers, partners)   → behind VITE_EXTERNAL_SIGNIN=1
// In production customers arrive through the Lipari customer portal and the tab inherits that
// identity; the direct external sign-in exists so the customer path can be tested before then.
import { PublicClientApplication } from "@azure/msal-browser";

const env = import.meta.env;
export const devBypass = env.VITE_AUTH_DEV_BYPASS === "1" && env.DEV;

const SYSTEM_KEY = "foodshow.authSystem";
const AUTO_KEY = "foodshow.autoLogin";
const ss = {
  get: (k) => { try { return sessionStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { sessionStorage.setItem(k, v); } catch { /* private mode */ } },
  del: (k) => { try { sessionStorage.removeItem(k); } catch { /* ignore */ } },
};

const systems = {
  internal: {
    label: "Lipari employee",
    clientId: env.VITE_ENTRA_WEB_CLIENT_ID ?? "",
    authority: `https://login.microsoftonline.com/${env.VITE_ENTRA_TENANT_ID ?? "common"}`,
    knownAuthorities: [],
    scopes: [env.VITE_ENTRA_API_SCOPE].filter(Boolean),
  },
  external: {
    label: "Customer or partner",
    clientId: env.VITE_ENTRA_EXTERNAL_WEB_CLIENT_ID ?? "",
    // Full authority, e.g. https://liparinonprod.ciamlogin.com/<tenant id>/
    authority: env.VITE_ENTRA_EXTERNAL_AUTHORITY ?? "",
    knownAuthorities: env.VITE_ENTRA_EXTERNAL_AUTHORITY ? [new URL(env.VITE_ENTRA_EXTERNAL_AUTHORITY).host] : [],
    scopes: [env.VITE_ENTRA_EXTERNAL_API_SCOPE].filter(Boolean),
  },
};

export const externalEnabled = env.VITE_EXTERNAL_SIGNIN === "1" && Boolean(systems.external.clientId && systems.external.authority);
export const system = ss.get(SYSTEM_KEY) === "external" && externalEnabled ? "external" : "internal";
const active = systems[system];

export const identityConfigured = Boolean(active.clientId);
export const msalInstance = devBypass || !identityConfigured ? null : new PublicClientApplication({
  auth: { clientId: active.clientId, authority: active.authority, knownAuthorities: active.knownAuthorities, redirectUri: window.location.origin, postLogoutRedirectUri: window.location.origin },
  cache: { cacheLocation: "sessionStorage" },
});
export const loginRequest = { scopes: active.scopes };

/** Switch identity system for this session. Rebuilding MSAL needs a reload; sign-in resumes after it. */
export function startSignIn(which) {
  if (which !== system) {
    ss.set(SYSTEM_KEY, which);
    ss.set(AUTO_KEY, "1");
    window.location.reload();
    return;
  }
  return msalInstance.loginRedirect(loginRequest);
}
export function consumeAutoLogin() { const v = ss.get(AUTO_KEY) === "1"; ss.del(AUTO_KEY); return v; }

// Roles live in the access token, so a cached token keeps yesterday's roles for up to an hour.
// Refresh once per page load so a role granted since the last visit takes effect on reload.
let refreshedThisLoad = false;

export async function getAccessToken() {
  if (devBypass) return null;
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) throw new Error("Not signed in");
  try {
    const forceRefresh = !refreshedThisLoad;
    refreshedThisLoad = true;
    const { accessToken } = await msalInstance.acquireTokenSilent({ ...loginRequest, account, forceRefresh });
    return accessToken;
  } catch {
    await msalInstance.acquireTokenRedirect({ ...loginRequest, account });
    return null; // page is navigating away
  }
}

export function signOut() {
  if (devBypass) return;
  ss.del(SYSTEM_KEY);
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  return msalInstance.logoutRedirect({ account, postLogoutRedirectUri: window.location.origin });
}
